/**
 * Ingestion Chart Component
 *
 * Stacked area chart showing document ingestion by source over time
 */

import { Area, AreaChart, CartesianGrid, Legend, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { formatChartTime } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import { createDotProps, createActiveDotProps } from './chart-utils';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { IngestionBySourcePoint } from '@/types/dashboard';

interface IngestionChartProps {
  data: IngestionBySourcePoint[] | undefined;
  isLoading: boolean;
}

const chartConfig = {
  fathom: {
    label: 'Fathom',
    color: 'var(--chart-1)',
  },
  helpscout: {
    label: 'HelpScout',
    color: 'var(--chart-2)',
  },
  basecamp: {
    label: 'Basecamp',
    color: 'var(--chart-3)',
  },
} satisfies ChartConfig;

export function IngestionChart({ data, isLoading }: IngestionChartProps) {
  // Transform data for recharts
  const chartData =
    data?.map((point) => ({
      time: formatChartTime(point.timestamp),
      fathom: point.fathom,
      helpscout: point.helpscout,
      basecamp: point.basecamp,
    })) ?? [];

  // Check if we have any data with values
  const hasData = chartData.some(
    (d) => d.fathom > 0 || d.helpscout > 0 || d.basecamp > 0,
  );

  // Show skeleton when loading OR when data hasn't arrived yet (prop is undefined)
  const showSkeleton = isLoading || data === undefined;

  // Show "no data" only when data has loaded but is genuinely empty
  const isEmpty = !showSkeleton && (chartData.length === 0 || !hasData);

  return (
    <ChartCard
      title="Ingestion by Source"
      tooltip={METRIC_DESCRIPTIONS.ingestionBySource}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyAriaLabel="No ingestion data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <AreaChart data={chartData} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            allowDecimals={false}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Legend />
          <Area
            dataKey="fathom"
            name="Fathom"
            type="monotone"
            stackId="1"
            fill="var(--color-fathom)"
            fillOpacity={0.4}
            stroke="var(--color-fathom)"
            strokeWidth={2}
            dot={createDotProps(chartData.length, 'var(--color-fathom)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-fathom)',
            )}
          />
          <Area
            dataKey="helpscout"
            name="HelpScout"
            type="monotone"
            stackId="1"
            fill="var(--color-helpscout)"
            fillOpacity={0.4}
            stroke="var(--color-helpscout)"
            strokeWidth={2}
            dot={createDotProps(chartData.length, 'var(--color-helpscout)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-helpscout)',
            )}
          />
          <Area
            dataKey="basecamp"
            name="Basecamp"
            type="monotone"
            stackId="1"
            fill="var(--color-basecamp)"
            fillOpacity={0.4}
            stroke="var(--color-basecamp)"
            strokeWidth={2}
            dot={createDotProps(chartData.length, 'var(--color-basecamp)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-basecamp)',
            )}
          />
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}
